<template>
  <div class="d-flex overflow-x-auto align-items-start mb-4">
    <!-- Column per stage -->
    <div
      class="kanban-list-items bg-white"
      v-for="col in columns"
      :key="col.id"
    >
      <div class="card mb-0">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h4 class="fw-medium d-flex align-items-center mb-1">
                <i :class="col.dotClass"></i>{{ col.title }}
              </h4>
              <span class="fw-normal text-default">{{ col.countLabel }}</span>
            </div>
            <div class="d-flex align-items-center">
              <div class="action-icon d-inline-flex">
                <a href="javascript:void(0);" @click="openAdd(col.id)" v-can="'Deal.Create'"><i class="ti ti-circle-plus"></i></a>
                <a href="javascript:void(0);" @click="openEditColumn(col.id)" v-can="'Deal.Edit'"><i class="ti ti-edit"></i></a>
                <a href="javascript:void(0);" @click="openDeleteColumn(col.id)" v-can="'Deal.Delete'"><i class="ti ti-trash"></i></a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cards -->
      <div class="kanban-drag-wrap pt-4">
        <draggable
          :list="col.cards"
          :group="{ name: 'kanban', pull: true, put: true }"
          item-key="id"
          @end="onDragEnd($event, col)"
        >
          <template #item="{ element: Card }">
            <div>
              <div class="card kanban-card">
                <div class="card-body">
                  <div class="d-block">
                    <div :class="borderClass(col)"></div>
                    <div class="d-flex align-items-center mb-3">
                      <router-link
                        :to="{ name: 'crm.deals.details', params: { id: Card.id } }"
                        class="avatar avatar-lg bg-gray flex-shrink-0 me-2"
                      >
                        <span class="avatar-title text-dark">{{ initials(Card.name) }}</span>
                      </router-link>
                      <h6 class="fw-medium">
                        <router-link :to="{ name: 'crm.deals.details', params: { id: Card.id } }">
                          {{ Card.name }}
                        </router-link>
                      </h6>
                    </div>
                  </div>

                  <div class="mb-3 d-flex flex-column">
                    <p class="text-default d-inline-flex align-items-center mb-2">
                      <i class="ti ti-currency-dollar text-dark me-2"></i>
                      {{ money(Card.amount, Card.currency) }}
                    </p>
                    <p class="text-default d-inline-flex align-items-center mb-2" v-if="Card.contact_email">
                      <i class="ti ti-mail text-dark me-2"></i>
                      {{ Card.contact_email }}
                    </p>
                    <p class="text-default d-inline-flex align-items-center mb-2" v-if="Card.contact_phone">
                      <i class="ti ti-phone text-dark me-2"></i>
                      {{ Card.contact_phone }}
                    </p>
                    <p class="text-default d-inline-flex align-items-center" v-if="Card.company_name">
                      <i class="ti ti-building text-dark me-2"></i>
                      {{ Card.company_name }}
                    </p>
                  </div>

                  <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <span class="avatar avatar-md avatar-rounded flex-shrink-0 me-2 bg-light d-inline-flex align-items-center justify-content-center">
                        {{ ownerInitials(Card) }}
                      </span>
                      <span class="text-dark">
                        {{ ownerName(Card) }}
                      </span>
                    </div>

                    <span class="badge badge-sm"
                          :class="probClass(Card.probability)">
                      <i class="ti ti-progress me-1"></i>{{ Card.probability != null ? (Card.probability + '%') : '-' }}
                    </span>
                  </div>

                  <div class="d-flex align-items-center justify-content-between border-top pt-3 mt-3">
                    <span class="text-dark">
                      <i class="ti ti-calendar-due text-gray-5"></i>
                      {{ formatDate(Card.expected_close_date, 'date') }}
                    </span>
                    <div class="d-flex align-items-center">
                      <router-link :to="{ name: 'crm.deals.details', params: { id: Card.id } }" class="d-flex align-items-center justify-content-center me-2">
                        <i class="ti ti-phone-check"></i>
                      </router-link>
                      <a href="javascript:void(0);" class="d-flex align-items-center justify-content-center me-2">
                        <i class="ti ti-message-circle-2"></i>
                      </a>
                      <a href="javascript:void(0);" class="d-flex align-items-center justify-content-center">
                        <i class="ti ti-color-swatch"></i>
                      </a>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </template>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { VueDraggableNext as draggable } from "vue-draggable-next";
import { useDealStore } from "@/stores/dealStore";

const dealStore = useDealStore();

const stageColors = [
  { dot: "ti ti-circle-filled fs-8 text-purple me-2", border: "border-purple border border-2 mb-3" },
  { dot: "ti ti-circle-filled fs-8 text-skyblue me-2", border: "border-skyblue border border-2 mb-3" },
  { dot: "ti ti-circle-filled fs-8 text-warning me-2", border: "border-warning border border-2 mb-3" },
  { dot: "ti ti-circle-filled fs-8 text-pink me-2",   border: "border-pink border border-2 mb-3" },
  { dot: "ti ti-circle-filled fs-8 text-success me-2",border: "border-success border border-2 mb-3" },
  { dot: "ti ti-circle-filled fs-8 text-info me-2",   border: "border-info border border-2 mb-3" },
];

// Build dynamic columns from the stages present in the data.
// Uses stage_id; unknown/null stage goes to "Unassigned".
const columns = computed(() => {
  const items = (dealStore.items || []).map(d => dealStore.normalize ? dealStore.normalize(d) : d);
  // map stage_id -> { id, title, dotClass, borderClass, cards }
  const byStage = new Map();

  function ensureStage(stage) {
    const key = stage?.id ?? 0;
    if (!byStage.has(key)) {
      const idx = byStage.size % stageColors.length;
      byStage.set(key, {
        id: key,
        title: stage?.name || "Unassigned",
        dotClass: stageColors[idx].dot,
        borderClass: stageColors[idx].border,
        cards: [],
      });
    }
    return byStage.get(key);
  }

  for (const d of items) {
    const s = ensureStage(d.stage || { id: 0, name: "Unassigned" });
    s.cards.push(d);
  }

  // compute counts/labels
  const out = [];
  for (const s of byStage.values()) {
    const total = s.cards.length;
    const sum = s.cards.reduce((acc, x) => acc + Number(x.amount || 0), 0);
    const cur = s.cards[0]?.currency || "KES";
    s.countLabel = `${total} ${total === 1 ? "Deal" : "Deals"} - ${money(sum, cur)}`;
    out.push(s);
  }
  return out;
});

function borderClass(col) { return col.borderClass; }

function probClass(p) {
  if (p == null) return "badge-secondary";
  if (p >= 70) return "badge-success-transparent";
  if (p >= 40) return "badge-info-transparent";
  return "badge-warning-transparent";
}

function ownerName(d) {
  const o = d?.owner;
  if (!o) return "-";
  return o.first_name ? `${o.first_name} ${o.last_name || ""}` : (o.email || "-");
}
function ownerInitials(d) {
  const o = d?.owner;
  const s = o?.first_name ? `${o.first_name} ${o.last_name || ""}` : (o?.email || "OW");
  return initials(s);
}

function initials(name) {
  if (!name) return "DL";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join("");
}
function money(amount, currency = "KES") {
  const n = Number(amount ?? 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString()}`;
  }
}
function formatDate(val, mode = "datetime") {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return mode === "date" ? d.toISOString().slice(0, 10) : d.toLocaleString();
}

/* Actions on toolbar (column-level) – keep as stubs for now */
function openAdd(stageId) { /* open create modal preselecting stageId if you want */ }
function openEditColumn(stageId) { /* edit stage settings */ }
function openDeleteColumn(stageId) { /* delete stage (if allowed) */ }

/* Drag handling: move card to new column => update stage_id */
async function onDragEnd(evt, targetCol) {
  // evt contains .item (DOM) and indexes; draggable already moved items in UI
  const moved = evt.item && evt.item.__vue__ ? evt.item.__vue__ : null;
  // But we already have latest order in `columns`, just update backend stage_id for each card in target column.
  // We only need to update the single moved element; easiest is to read from evt.draggedContext if available
  const ctx = evt?.draggedContext;
  const card = ctx?.element;
  if (!card || targetCol?.id == null) return;

  try {
    // optimistic: reflect stage change locally
    card.stage_id = targetCol.id;
    card.stage = { ...(card.stage || {}), id: targetCol.id, name: targetCol.title };

    // persist
    await dealStore.update(card.id, { stage_id: targetCol.id });
  } catch (e) {
    // reload to revert if failed
    await dealStore.fetchAll();
  }
}
</script>

<script>
/* Register draggable as local component (when using <script setup>, do it via `components` option) */
export default { components: { draggable: (await import("vue-draggable-next")).VueDraggableNext } };
</script>
